import type { Framing } from '../components/StudioImage'

export type CatalogClass = {
  classId: string
  name: string
  style: string | null
  description: string | null
  dayOfWeek: string
  startTime: string | null
  endTime: string | null
  ageLabel: string
  minAgeMonths: number | null
  maxAgeMonths: number | null
  teachers: string[]
  availability: string
  isFull: boolean
  canRegister: boolean
  /** Only present when the studio discloses exact counts. */
  spotsRemaining: number | null
  /** Jackrabbit's class id, when we have one. See {@link registerHref}. */
  registrationId: string | null
  /** Which kind of class this is, for grouping (D-191). */
  classTypeId: string | null
}

/**
 * A kind of class, as a prospective parent meets it (D-191).
 *
 * The unit a family actually chooses in: seven Tippy Toes sections are one
 * decision followed by a time, not seven decisions.
 */
export type CatalogClassType = {
  classTypeId: string
  name: string
  blurb: string | null
  /** Already a URL. Null when nobody has chosen a photograph yet. */
  heroUrl: string | null
  /** Resolved server-side: this placement's override, else the photo's own (D-199). */
  heroFraming: Framing | null
  /** Spans every class inside it, so the heading answers "is this for my child?" */
  ageLabel: string | null
  /**
   * The description most of these classes share, said once (D-212).
   *
   * Classes carrying exactly this have it stripped from their own card; one
   * that says something different keeps saying it.
   */
  sharedDescription: string | null
  /** Youngest first — the age is what a parent is scanning for (D-212). */
  classes: CatalogClass[]
}

/**
 * Where the Register button goes.
 *
 * Registration runs through Jackrabbit while it still owns billing (D-111).
 * Given their class id we can open their form with the class **already
 * chosen** — without one we send the parent to the plain form, where they pick
 * it themselves. Degraded, never broken.
 *
 * `base` is the content block Tonya can edit, so the day Jackrabbit goes this
 * whole thing is one field.
 */
export function registerHref(base: string, registrationId: string | null): string {
  if (!base) return ''
  if (!registrationId) return base

  // Their form is `reg.asp`; the listing embed is a different path. Only add
  // the class when we are pointed at something that would understand it —
  // otherwise a stray parameter rides along to wherever the link now goes.
  if (!/reg(v2)?\.asp/i.test(base)) return base

  return `${base}${base.includes('?') ? '&' : '?'}preLoadClassID=${encodeURIComponent(registrationId)}`
}

export type PublicCatalog = {
  classes: CatalogClass[]
  /** The same classes, grouped into what the studio calls them (D-191). */
  types: CatalogClassType[]
  /** Belonging to no type. Listed rather than dropped — never hidden. */
  ungrouped: CatalogClass[]
  days: string[]
  styles: string[]
  registrationOpen: boolean
  registrationClosesOn: string | null
  seasonName: string | null
}

export type CatalogQuery = {
  day?: string
  style?: string
  age?: number
  hideFull?: boolean
}

export async function fetchCatalog(query: CatalogQuery = {}): Promise<PublicCatalog> {
  const params = new URLSearchParams()
  if (query.day) params.set('day', query.day)
  if (query.style) params.set('style', query.style)
  if (query.age !== undefined) params.set('age', String(query.age))
  if (query.hideFull) params.set('hideFull', 'true')

  const res = await fetch(`/api/catalog?${params}`)
  if (!res.ok) throw new Error(`Could not load classes (${res.status})`)
  return res.json()
}

/**
 * Groups classes by weekday for display, Monday first.
 *
 * A flat list of 28 classes is a wall; a parent scans by "what runs on the day
 * I can drive". Only days that have something appear, so the page never shows
 * an empty Friday heading.
 */
export function byDay(classes: CatalogClass[]): { day: string; classes: CatalogClass[] }[] {
  const order = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

  return order
    .map((day) => ({ day, classes: classes.filter((c) => c.dayOfWeek === day) }))
    .filter((group) => group.classes.length > 0)
}

/** "4:15pm – 5:00pm", or "Time to be confirmed". */
export function timeRange(start: string | null, end: string | null): string {
  if (!start) return 'Time to be confirmed'

  const clock = (time: string) => {
    const [h, m] = time.split(':').map(Number)
    const hour = h % 12 === 0 ? 12 : h % 12
    return `${hour}:${String(m).padStart(2, '0')}${h >= 12 ? 'pm' : 'am'}`
  }

  return end ? `${clock(start)} – ${clock(end)}` : clock(start)
}

/**
 * How urgent the availability reads.
 *
 * Drives colour only. The words come from the server, because how much to
 * disclose is Tonya's setting and the browser must not be able to say more
 * than she chose.
 */
export function availabilityTone(item: CatalogClass): 'full' | 'low' | 'open' {
  if (item.isFull) return 'full'
  if (item.spotsRemaining !== null && item.spotsRemaining <= 3) return 'low'
  if (item.availability === 'Almost full') return 'low'
  return 'open'
}

/**
 * A plain-English summary of the filters in force.
 *
 * Shown when a filter returns nothing, so "no classes" is never mistaken for
 * "this studio has no classes".
 */
export function describeQuery(query: CatalogQuery): string {
  const parts: string[] = []
  if (query.age !== undefined) parts.push(`age ${query.age}`)
  if (query.day) parts.push(`on ${query.day}`)
  if (query.style) parts.push(query.style)
  if (query.hideFull) parts.push('with spaces left')

  return parts.join(', ')
}
